"use client";

import Image, { getImageProps } from "next/image";
import { ImageType, isAvailable, LinkType } from "../../../types/baseTypes";
import styles from "./MediaItem.module.scss";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { generateMediaFormat } from "../../../utils/layout/mediaFormat";
import { ComponentProps, useState } from "react";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import { CustomLink } from "../CustomLink";
import MediaInfo from "@/components/MediaInfo/MediaInfo";

interface MediaItemProps {
  image_fullhd?: ImageType | false;
  image_desktop: ImageType | false;
  image_tablet?: ImageType | false;
  image_mobile?: ImageType | false;
  sizes: string | undefined;
  quality?: number;
  mediaFormat?: MediaFormatType;
  showPlayIcon?: boolean;
  priority?: boolean;
  className?: string;
  copyrightPosition?: "top" | "bottom";
  showCopyright?: boolean;
  objectFit?: "cover" | "contain";
  link?: LinkType;
  linkProps?: ComponentProps<"a">;
  disablePrefetch?: boolean;
  showModal?: boolean;
}

const generateImageProps = ({
  image,
  sizes,
  quality,
  priority = false,
}: {
  image?: ImageType | false;
  sizes?: string;
  quality: number;
  priority?: boolean;
}) => {
  if (!image) return { srcSet: undefined, rest: undefined };

  const blurDataURL =
    isAvailable<ImageType>(image) && image.image_blur && image.mime_type
      ? `data:${image.mime_type};base64,${image.image_blur}`
      : undefined;

  const {
    props: { srcSet: srcSet, ...rest },
  } = getImageProps({
    width: image.width,
    height: image.height,
    quality,
    src: image.url,
    alt: image.alt || image.title,
    blurDataURL: blurDataURL,
    placeholder: blurDataURL ? "blur" : undefined,
    sizes: sizes,
    priority,
  });

  return { srcSet, rest: rest };
};

export const MediaItem = ({
  image_fullhd,
  image_desktop,
  image_tablet,
  image_mobile,
  sizes,
  quality = 75,
  mediaFormat,
  /*copyrightPosition,*/
  showPlayIcon = false,
  priority = false,
  className = "",
  /*showCopyright,*/
  objectFit = "cover",
  link,
  linkProps,
  disablePrefetch,
  showModal = true,
}: MediaItemProps) => {
  const isSVG = image_desktop
    ? (image_desktop.mime_type?.includes("svg") ?? false)
    : false;
  const [load, setLoad] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const { getIcon } = useGlobalContext();
  /*const images = {
    imgfhd: image_fullhd ?? false,
    imgdsk: image_desktop ?? false,
    imgtab: image_tablet ?? false,
    imgmob: image_mobile ?? false,
  };*/

  const { srcSet: fullhdSrcSet, rest: fullHdRest } = generateImageProps({
    image: image_fullhd,
    sizes,
    quality,
    priority,
  });

  const { srcSet: desktopSrcSet, rest: desktopRest } = generateImageProps({
    image: image_desktop,
    sizes,
    quality,
    priority,
  });
  const { srcSet: tabletSrcSet } = generateImageProps({
    image: image_tablet,
    sizes,
    quality,
    priority,
  });
  const { srcSet: mobileSrcSet } = generateImageProps({
    image: image_mobile,
    sizes,
    quality,
    priority,
  });

  const imageProps = desktopRest || fullHdRest;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isExactlyOneDefined(...objs: any[]) {
    const definedObjects = objs.filter((obj) => obj);
    // Check if exactly one object is defined
    if (definedObjects.length === 1) {
      return definedObjects[0];
    }
    return;
  }

  const onlyOneImage = isExactlyOneDefined(
    image_fullhd,
    image_desktop,
    image_tablet,
    image_mobile,
  );

  const fetchPriority = imageProps?.fetchPriority;
  if (fetchPriority) {
    delete imageProps.fetchPriority;
  }

  const ImageJSX = ({
    overwriteSizes,
    className,
  }: {
    overwriteSizes?: string;
    className?: string;
  }) =>
    onlyOneImage ? (
      <Image
        src={onlyOneImage.url}
        alt={onlyOneImage.alt || onlyOneImage.title}
        sizes={overwriteSizes ?? sizes}
        quality={quality}
        height={onlyOneImage.height}
        width={onlyOneImage.width}
        priority={priority}
        style={{ objectFit: objectFit, width: "100%", height: "100%" }}
        onError={() => setImageFailed(true)}
        className={className}
      />
    ) : (
      <picture>
        {image_fullhd && (
          <source
            media={
              !image_desktop && !image_tablet && !image_mobile
                ? undefined
                : !image_desktop && !image_tablet && image_mobile
                  ? "(min-width: 768px)"
                  : !image_desktop && image_tablet && image_mobile
                    ? "(min-width: 1024px)"
                    : "(min-width: 1408px)"
            }
            srcSet={fullhdSrcSet ?? image_fullhd.url}
            width={image_fullhd.width}
            height={image_fullhd?.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {image_desktop && (
          <source
            media={
              !image_tablet && !image_mobile
                ? undefined
                : !image_tablet && image_mobile
                  ? "(min-width: 768px)"
                  : "(min-width: 1024px)"
            }
            srcSet={desktopSrcSet ?? image_desktop.url}
            width={image_desktop.width}
            height={image_desktop.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {image_tablet && (
          <source
            media={!image_mobile ? undefined : "(min-width: 768px)"}
            srcSet={tabletSrcSet ?? image_tablet.url}
            width={image_tablet.width}
            height={image_tablet.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {image_mobile && (
          <source
            srcSet={mobileSrcSet ?? image_mobile.url}
            width={image_mobile.width}
            height={image_mobile.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {/* wrong typed I guess because alt is inside rest */}
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          {...imageProps}
          style={{
            ...imageProps?.style,
            width: "100%",
            height: "100%",
            objectFit: objectFit,
            ...(load && { backgroundImage: "unset" }),
          }}
          onLoad={() => {
            setLoad(true);
          }}
          {...(fetchPriority && { fetchPriority: fetchPriority })}
        />
      </picture>
    );

  const classNameString = [
    "image-wrapper",
    styles["image-wrapper"],
    className,
    mediaFormat && !isSVG ? generateMediaFormat(mediaFormat) : "",
  ]
    .join(" ")
    .trim();

  return (
    <div className={classNameString}>
      <CustomLink
        alwaysRenderChildren
        href={link ? link.url : undefined}
        target={link ? link.target : "_self"}
        disablePrefetch={disablePrefetch}
        {...linkProps}
      >
        <ImageJSX className={imageFailed ? styles["image-failed"] : ""} />
      </CustomLink>

      {showPlayIcon && (
        <>
          <div className={styles["has-gradient"]}> </div>
          <div className={styles["play-icon"]}>
            <ReactSVG
              src={getIcon("play")}
              wrapper={"svg"}
              width={16}
              height={16}
              aria-hidden
            />
          </div>
        </>
      )}

      {/* <Copyright
        {...images}
        copyrightPosition={copyrightPosition}
        showCopyright={showCopyright}
      />*/}

      {showModal && (
        <MediaInfo
          image_desktop={image_desktop}
          image_fullhd={image_fullhd}
          image_tablet={image_tablet}
          image_mobile={image_mobile}
          media={
            <div className={classNameString}>
              <ImageJSX overwriteSizes="100vw" />
            </div>
          }
        />
      )}
    </div>
  );
};
